import {
  isArray,
  partition,
} from 'lodash';
import * as color from '../sass/color.scss';

const SOURCE_CHIP_COLORS = [
  color.lightOrchid,
  color.rainboots,
  color.royalBlue,
  color.graphBlue,
  color.boxBlue,
  color.teal,
  color.success,
  color.sushi,
  color.orange,
  color.carrot,
  color.boxPink,
  color.red,
];

/**
 * List of supported External ID's
 * @param options object of options to build the list
 * @param options.includeEmpty boolean to include an empty name/value pair at the beginning
 * @param options.emptyData object of data to spread into the empty name/value pair if includeEmpty is true
 */
export function list(options = {}) {
  const list = [
    {
      label: 'AME',
      value: 'ame',
    },
    {
      label: 'Harmony',
      value: 'harmony',
    },
    {
      label: 'Stingray',
      value: 'stingray',
    },
  ];

  if (options.includeEmpty === true) {
    const emptyData = options.emptyData ?? {};

    list.unshift({
      label: '',
      value: '',
      ...emptyData,
    });
  }

  return list;
}

export function normalizeExternalId(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  return {
    source: item.source || '',
    extId: item.extId || '',
  };
}

export function formatChipLabel(externalId) {
  const {
    source,
    extId,
  } = normalizeExternalId(externalId) || {};

  if (!extId) {
    return '';
  }

  return source
    ? `${source}: ${extId}`
    : `${extId}`;
}

function hashString(value) {
  return Array.from(value).reduce((hash, character) => {
    return ((hash << 5) - hash) + character.charCodeAt(0) | 0;
  }, 0);
}

function hexToRgba(hex, alpha) {
  const normalized = hex.replace('#', '');
  const fullHex = normalized.length === 3
    ? normalized.split('').map(character => `${character}${character}`).join('')
    : normalized;
  const bigint = parseInt(fullHex, 16);

  return `rgba(${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}, ${alpha})`;
}

export function getSourceColor(source) {
  const key = (source || '').toLowerCase();

  if (key === 'vibenomics') {
    return {
      color: color.boxPurple,
      background: color.boxPurple16,
    };
  }

  const chipColor = SOURCE_CHIP_COLORS[Math.abs(hashString(key)) % SOURCE_CHIP_COLORS.length];

  return {
    color: chipColor,
    background: hexToRgba(chipColor, 0.16),
  };
}

function normalizeFilterValues(filterValues) {
  if (filterValues == null || filterValues === '') {
    return [];
  }

  const values = isArray(filterValues)
    ? filterValues
    : [filterValues];

  return values
    .filter(value => value != null && value !== '')
    .map(value => value.toString().toLowerCase());
}

function matchesFilter(extId, filters) {
  const id = (extId || '').toString().toLowerCase();

  return filters.some(filter => id.indexOf(filter) >= 0);
}

export function matchesExternalIdFilter(externalIds, filterValue) {
  const filters = normalizeFilterValues(filterValue);

  if (filters.length === 0) {
    return false;
  }

  return (externalIds || []).some((item) => {
    const normalized = normalizeExternalId(item);
    return normalized && matchesFilter(normalized.extId, filters);
  });
}

/**
 * Order external IDs for table display.
 * Matching filter values come first when searching; otherwise vibenomics is first.
 * Relative order of the remaining items is preserved.
 */
export function sortExternalIds(externalIds = [], filterValues) {
  const ids = (externalIds || [])
    .map(normalizeExternalId)
    .filter(Boolean);

  const filters = normalizeFilterValues(filterValues);

  if (filters.length > 0) {
    const [matches, rest] = partition(ids, id => matchesFilter(id.extId, filters));
    return [...matches, ...rest];
  }

  const [vibenomics, rest] = partition(
    ids,
    id => (id.source || '').toLowerCase() === 'vibenomics',
  );

  return [...vibenomics, ...rest];
}

export default {
  list,
  normalizeExternalId,
  formatChipLabel,
  getSourceColor,
  sortExternalIds,
  matchesExternalIdFilter,
};
